import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  FileOutlined,
} from "@ant-design/icons";
import {
  Badge,
  Button,
  Card,
  Col,
  Divider,
  Empty,
  Form,
  List,
  Row,
  Space,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import React, { memo, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import TextArea from "antd/es/input/TextArea";
import {
  updateStatusOfAdmissionRequirementsApiRequest,
  updateStatusOfEnrollmentRequirementsApiRequest,
} from "../../../../api/applicant";

const { Title, Text, Paragraph } = Typography;

const RequirementsSection = memo(
  ({
    admissionRequirements = [],
    enrollmentRequirements = [],
    voucherRequirements = [], // Added prop
    enrollmentRequirementsStatus = "Incomplete",
    admissionRequirementsStatus = "Incomplete",
    voucherApplicationStatus = "Incomplete", // Added prop
    applicantId,
    onStatusUpdate = () => {},
  }) => {
    const [rejectReason, setRejectReason] = useState("");
    const [showRejectForm, setShowRejectForm] = useState(false);
    const [rejectType, setRejectType] = useState("admission");

    // Loading states
    const [isLoading, setIsLoading] = useState({
      admission: false,
      enrollment: false,
      reject: false,
    });

    const [admissionStatus, setAdmissionStatus] = useState(
      admissionRequirementsStatus
    );
    const [enrollmentStatus, setEnrollmentStatus] = useState(
      enrollmentRequirementsStatus
    );

    // Update local state when props change
    useEffect(() => {
      setAdmissionStatus(admissionRequirementsStatus);
      setEnrollmentStatus(enrollmentRequirementsStatus);
    }, [admissionRequirementsStatus, enrollmentRequirementsStatus]);

    const hasAdmissionRequirements = admissionRequirements && admissionRequirements.length > 0;
    const hasEnrollmentRequirements =
      enrollmentRequirements && enrollmentRequirements.length > 0;
    const hasVoucherRequirements =
      voucherRequirements && voucherRequirements.length > 0; // Added check

    const formatFileSize = (bytes) => {
      if (!bytes) return "Unknown size";
      const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
      if (bytes === 0) return "0 Byte";
      const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
      return Math.round(bytes / Math.pow(1024, i), 2) + " " + sizes[i];
    };

    const formatDate = (dateString) => {
      if (!dateString) return "Unknown date";
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    console.log("enrollmentRequirements", enrollmentRequirements);
    const getStatusTagColor = (status) => {
      switch (status?.toLowerCase()) {
        case "submitted":
          return "blue";
        case "approved":
          return "green";
        case "rejected":
          return "red";
        case "pending":
          return "orange";
        case "complete":
          return "green";
        default:
          return "default";
      }
    };

    const voucherRequirementsList = useMemo(() => {
      return hasVoucherRequirements
        ? voucherRequirements.map((item) => ({
            ...item,
            statusColor: getStatusTagColor(item.status),
            formattedSize: formatFileSize(item.fileSize),
            formattedDate: formatDate(item.uploadDate),
            fileViewUrl: item.voucherRequirementsFileUrl || null, // Use voucherRequirementsFileUrl
          }))
        : [];
    }, [voucherRequirements, hasVoucherRequirements]);

    const enrollmentRequirementsList = useMemo(() => {
      return hasEnrollmentRequirements
        ? enrollmentRequirements.map((item) => {
            // Check if we have a fileUrl directly, or need to construct one from supabaseUrl
            let fileViewUrl = item.fileUrl;

            // If no fileUrl but we have a fileName and supabaseUrl, construct the URL
            if (!fileViewUrl && item.fileName && item.supabaseUrl) {
              fileViewUrl = item.supabaseUrl;
            }

            // If no direct URLs, but we have fileName, userEmail, and requirementId, try to construct Supabase URL
            if (!fileViewUrl && item.fileName && item.userEmail && item.requirementId) {
              const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
              if (supabaseUrl) {
                fileViewUrl = `${supabaseUrl}/storage/v1/object/public/enrollment-documents/${item.userEmail}/${item.requirementId}/${item.fileName}`;
              }
            }

            // Log the constructed URL for debugging
            console.log("File View URL for", item.name, ":", fileViewUrl);

            return {
              ...item,
              statusColor: getStatusTagColor(item.status),
              formattedSize: formatFileSize(item.fileSize),
              formattedDate: formatDate(item.uploadDate),
              fileViewUrl: fileViewUrl || item.imageUrl, // Use imageUrl as fallback if available
            };
          })
        : [];
    }, [enrollmentRequirements, hasEnrollmentRequirements]);

    const admissionRequirementsList = useMemo(() => {
      return hasAdmissionRequirements
        ? admissionRequirements.map((item) => ({
            ...item,
            statusColor: getStatusTagColor(item.status),
            formattedSize: formatFileSize(item.fileSize),
            formattedDate: formatDate(item.uploadDate),
          }))
        : [];
    }, [admissionRequirements, hasAdmissionRequirements]);

    // Handle approving requirements (admission or enrollment)
    const handleApproveRequirements = async (type) => {
      console.log("ss", type);
      if (!applicantId) {
        toast.error("Applicant ID is missing");
        return;
      }

      const loadingKey = type; // 'admission' or 'enrollment'
      const setStatusFn =
        type === "admission" ? setAdmissionStatus : setEnrollmentStatus;

      try {
        // Set loading state
        setIsLoading((prev) => ({ ...prev, [loadingKey]: true }));

        // Call the appropriate API
        const apiRequest =
          type === "admission"
            ? updateStatusOfAdmissionRequirementsApiRequest
            : updateStatusOfEnrollmentRequirementsApiRequest;
        console.log("type", type);
        await apiRequest(applicantId, "Approved");

        // Update UI state
        setStatusFn("Approved");
        onStatusUpdate(type, "Approved");

        // Show success toast
        toast.success(
          `${
            type === "admission" ? "Admission" : "Enrollment"
          } requirements have been approved`
        );
      } catch (error) {
        console.error(`Error approving ${type} requirements:`, error);
        toast.error(
          `Failed to approve ${type} requirements: ${
            error.message || "Unknown error"
          }`
        );
      } finally {
        // Reset loading state
        setIsLoading((prev) => ({ ...prev, [loadingKey]: false }));
      }
    };

    // Handle rejecting requirements
    const handleRejectRequirements = async () => {
      if (!applicantId) {
        toast.error("Applicant ID is missing");
        return;
      }

      try {
        setIsLoading((prev) => ({ ...prev, reject: true }));

        const apiRequest =
          rejectType === "admission"
            ? updateStatusOfAdmissionRequirementsApiRequest
            : updateStatusOfEnrollmentRequirementsApiRequest;

        const response = await apiRequest(
          applicantId,
          "Rejected",
          rejectReason
        );

        // Update UI state
        if (rejectType === "admission") {
          setAdmissionStatus("Rejected");
        } else {
          setEnrollmentStatus("Rejected");
        }

        onStatusUpdate(rejectType, "Rejected", rejectReason);

        // Show success toast
        toast.success(
          `${
            rejectType === "admission" ? "Admission" : "Enrollment"
          } requirements have been rejected`
        );

        // Reset form
        setShowRejectForm(false);
        setRejectReason("");
      } catch (error) {
        console.error(`Error rejecting ${rejectType} requirements:`, error);
        toast.error(
          `Failed to reject ${rejectType} requirements: ${
            error.message || "Unknown error"
          }`
        );
      } finally {
        setIsLoading((prev) => ({ ...prev, reject: false }));
      }
    };

    const showRejectFormForType = (type) => {
      setRejectType(type);
      setShowRejectForm(true);
    };

    const handleViewFile = (fileUrl) => {
      if (!fileUrl) {
        toast.error("View URL not available");
        return;
      }
      window.open(fileUrl, "_blank");
    };

    // Create tabs items for Antd v5
    const tabItems = [
      {
        key: "1",
        label: (
          <span>
            Admission Requirements
            {hasAdmissionRequirements && (
              <Badge
                count={admissionRequirements.length}
                className="ml-2"
                color={
                  admissionStatus === "Approved" ||
                  admissionStatus === "Complete"
                    ? "green"
                    : admissionStatus === "Rejected"
                    ? "red"
                    : "blue"
                }
              />
            )}
          </span>
        ),
        children: hasAdmissionRequirements ? (
          <>
            <div className="requirements-grid">
              <List
                grid={{
                  gutter: 16,
                  xs: 1,
                  sm: 2,
                  md: 2,
                  lg: 3,
                  xl: 3,
                  xxl: 4,
                }}
                dataSource={admissionRequirementsList}
                renderItem={(file) => (
                  <List.Item
                    actions={[
                      file.imageUrl && (
                        <Tooltip title="View File">
                          <Button
                            type="link"
                            icon={<EyeOutlined />}
                            onClick={() => handleViewFile(file.imageUrl)}
                          />
                        </Tooltip>
                      ),
                    ]}
                  >
                    <List.Item.Meta
                      avatar={
                        <Badge dot color={file.statusColor} offset={[0, 0]}>
                          <FileOutlined style={{ fontSize: 24 }} />
                        </Badge>
                      }
                      title={
                        <>
                          <Text strong className="requirement-name">
                            {file.name}
                          </Text>
                          <Tag color={file.statusColor} className="ml-2">
                            {file.status}
                          </Tag>
                        </>
                      }
                      description={
                        <Space direction="vertical" size={0}>
                          <Text type="secondary" className="file-info">
                            {file.fileName || "No file name"}
                          </Text>
                          <Space size="large" className="file-meta">
                            <Text type="secondary" className="file-type">
                              Type: {file.fileType || "Unknown"}
                            </Text>
                            <Text type="secondary" className="file-size">
                              Requirement ID: {file.requirementId}
                            </Text>
                          </Space>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
                pagination={{
                  pageSize: 8,
                  simple: true,
                  position: "bottom",
                  hideOnSinglePage: true,
                }}
              />
            </div>

            <Divider />
          </>
        ) : (
          <Empty description="No admission requirements found" />
        ),
      },
      {
        key: "2",
        label: (
          <span>
            Enrollment Requirements
            {hasEnrollmentRequirements && (
              <Badge
                count={enrollmentRequirements.length}
                className="ml-2"
                color={
                  enrollmentStatus === "Approved" ||
                  enrollmentStatus === "Complete"
                    ? "green"
                    : enrollmentStatus === "Rejected"
                    ? "red"
                    : "blue"
                }
              />
            )}
          </span>
        ),
        children: hasEnrollmentRequirements ? (
          <>
            <List
              itemLayout="horizontal"
              dataSource={enrollmentRequirementsList}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    (item.fileViewUrl || item.imageUrl) && (
                      <Tooltip title="View File">
                        <Button
                          type="link"
                          icon={<EyeOutlined />}
                          onClick={() => handleViewFile(item.fileViewUrl || item.imageUrl)}
                        />
                      </Tooltip>
                    ),
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <Badge dot color={item.statusColor} offset={[0, 0]}>
                        <FileOutlined style={{ fontSize: 24 }} />
                      </Badge>
                    }
                    title={
                      <>
                        <Text strong className="requirement-name">
                          {item.name}
                        </Text>
                        <Tag color={item.statusColor} className="ml-2">
                          {item.status}
                        </Tag>
                      </>
                    }
                    description={
                      <Space direction="vertical" size={0}>
                        <Text type="secondary" className="file-info">
                          {item.fileName || "No file name"}
                        </Text>
                        <Space size="large" className="file-meta">
                          <Text type="secondary" className="file-type">
                            Type: {item.fileType || "Unknown"}
                          </Text>
                          <Text type="secondary" className="file-size">
                            Requirement ID: {item.requirementId}
                          </Text>
                        </Space>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />

            <Divider />

            <Row gutter={16}>
              <Col span={24}>
                <Title level={5}>Enrollment Requirements Review</Title>
                <Paragraph>
                  Review the applicant's enrollment documents and decide whether
                  to approve or reject them.
                </Paragraph>
              </Col>
              <Col span={24}>
                <Space>
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    loading={isLoading.enrollment}
                    onClick={() => handleApproveRequirements("enrollment")}
                    size="large"
                    disabled={isLoading.enrollment}
                  >
                    Approve Enrollment Documents
                  </Button>

                  <Button
                    danger
                    icon={<CloseCircleOutlined />}
                    onClick={() => showRejectFormForType("enrollment")}
                    size="large"
                    disabled={isLoading.enrollment}
                  >
                    Reject Enrollment Documents
                  </Button>
                </Space>

                {enrollmentStatus === "Approved" && (
                  <Tag color="green" className="mt-2">
                    These requirements have been approved
                  </Tag>
                )}

                {enrollmentStatus === "Rejected" && (
                  <Tag color="red" className="mt-2">
                    These requirements have been rejected
                  </Tag>
                )}

                {enrollmentStatus === "Complete" && (
                  <Tag color="green" className="mt-2">
                    These requirements are complete
                  </Tag>
                )}
              </Col>
            </Row>
          </>
        ) : (
          <Empty description="No enrollment requirements found" />
        ),
      },
      {
        key: "3",
        label: (
          <span>
            Voucher Requirements
            {hasVoucherRequirements && (
              <Badge
                count={voucherRequirements.length}
                className="ml-2"
                color={
                  voucherApplicationStatus === "Approved" ||
                  voucherApplicationStatus === "Complete"
                    ? "green"
                    : voucherApplicationStatus === "Rejected"
                    ? "red"
                    : "blue"
                }
              />
            )}
          </span>
        ),
        children: hasVoucherRequirements ? (
          <>
            <List
              itemLayout="horizontal"
              dataSource={voucherRequirementsList}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    item.fileViewUrl && (
                      <Tooltip title="View File">
                        <Button
                          type="link"
                          icon={<EyeOutlined />}
                          onClick={() => handleViewFile(item.fileViewUrl)}
                        />
                      </Tooltip>
                    ),
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <Badge dot color={item.statusColor} offset={[0, 0]}>
                        <FileOutlined style={{ fontSize: 24 }} />
                      </Badge>
                    }
                    title={
                      <>
                        <Text strong className="requirement-name">
                          {item.name}
                        </Text>
                        <Tag color={item.statusColor} className="ml-2">
                          {item.status}
                        </Tag>
                      </>
                    }
                    description={
                      <Space direction="vertical" size={0}>
                        <Text type="secondary" className="file-info">
                          {item.fileName || "No file name"}
                        </Text>
                        <Space size="large" className="file-meta">
                          <Text type="secondary" className="file-type">
                            Type: {item.fileType || "Unknown"}
                          </Text>
                          <Text type="secondary" className="file-size">
                            Requirement ID: {item.requirementId}
                          </Text>
                        </Space>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
            <Divider />
          </>
        ) : (
          <Empty description="No voucher requirements found" />
        ),
      },
    ];

    if (!hasAdmissionRequirements && !hasEnrollmentRequirements && !hasVoucherRequirements) {
      return (
        <Card>
          <Empty
            description="No requirements found"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      );
    }

    return (
      <Card className="requirements-card">
        <Row gutter={[0, 16]}>
          <Col span={24}>
            <Title level={4}>Applicant Requirements</Title>
            <Paragraph>
              Review the applicant's submitted requirements for both admission
              and enrollment.
            </Paragraph>
          </Col>

          <Col span={24}>
            {/* Updated to v5 Tabs format */}
            <Tabs
              defaultActiveKey="1"
              items={tabItems}
              destroyInactiveTabPane
              type="card"
            />
          </Col>

          {showRejectForm && (
            <Col span={24}>
              <Divider />
              <div className="mt-4">
                <Title level={5}>
                  {rejectType === "admission"
                    ? "Reject Admission Requirements"
                    : "Reject Enrollment Requirements"}
                </Title>
                <Form layout="vertical">
                
                  <Form.Item>
                    <Space>
                      <Button
                        onClick={() => {
                          setShowRejectForm(false);
                          setRejectReason("");
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="primary"
                        danger
                        loading={isLoading.reject}
                        onClick={handleRejectRequirements}
                        disabled={isLoading.reject}
                      >
                        Confirm Rejection
                      </Button>
                    </Space>
                  </Form.Item>
                </Form>
              </div>
            </Col>
          )}
        </Row>
      </Card>
    );
  }
);

export default RequirementsSection;